import type { LocationQuery, LocationQueryValue } from 'vue-router'

import type { VacancyCenterQuery } from '@/Network/Types/Requests/VacancyCenterRequests'
import type {
  VacancyBucket,
  VacancyStatusFilter
} from '@/Network/Types/Responses/VacancyCenterResponses'

export type VacancyGroupBy = 'urgency' | 'sector' | 'unit'

/** Filtros de vários valores da barra (SPEC 2.5a.1 §2.1). */
export type VacancyFilterKind = 'unit' | 'schedule' | 'sector' | 'specialty'

/**
 * Estado da tela (SPEC 2.5a.1 §5). Setor: `'sector:12'` | `'none'`;
 * especialidade: `'official:5'` | `'company:3'` | `'none'` (as `key` dos
 * contadores do backend). `tab` null = a primeira aba com itens.
 */
export interface VacancyViewState {
  status: VacancyStatusFilter
  days: number
  unitIds: number[]
  scheduleIds: number[]
  sectorKeys: string[]
  specialtyKeys: string[]
  groupBy: VacancyGroupBy
  tab: VacancyBucket | null
}

export const DEFAULT_VACANCY_VIEW_STATE: Readonly<VacancyViewState> = Object.freeze({
  status: 'all',
  days: 7,
  unitIds: [],
  scheduleIds: [],
  sectorKeys: [],
  specialtyKeys: [],
  groupBy: 'urgency',
  tab: null
})

/** Estado novo com listas próprias: ninguém compartilha array com o padrão. */
export function createVacancyViewState(
  overrides: Partial<VacancyViewState> = {}
): VacancyViewState {
  const base = { ...DEFAULT_VACANCY_VIEW_STATE, ...overrides }
  return {
    ...base,
    unitIds: [...base.unitIds],
    scheduleIds: [...base.scheduleIds],
    sectorKeys: [...base.sectorKeys],
    specialtyKeys: [...base.specialtyKeys]
  }
}

/** Chaves que a Central escreve/consome na URL (o resto da query é preservado). */
export const VACANCY_QUERY_KEYS = [
  'status',
  'dias',
  'unidade',
  'escala',
  'setor',
  'especialidade',
  'agrupar',
  'aba',
  'vaga'
] as const

const MIN_DAYS = 1
const MAX_DAYS = 31
const ID_PATTERN = /^[1-9]\d*$/
const NO_SECTOR = 'sem-setor'
const NO_SPECIALTY = 'sem-especialidade'

const GROUP_BY_TO_URL: Record<Exclude<VacancyGroupBy, 'urgency'>, string> = {
  sector: 'setor',
  unit: 'unidade'
}
const SPECIALTY_SOURCE_TO_URL = { official: 'oficial', company: 'empresa' } as const
const TAB_FROM_URL = new Map<string, VacancyBucket>([
  ['6h', 'next6h'],
  ['hoje', 'today'],
  ['amanha', 'tomorrow'],
  ['semana', 'week']
])
const TAB_TO_URL: Record<VacancyBucket, string> = {
  next6h: '6h',
  today: 'hoje',
  tomorrow: 'amanha',
  week: 'semana'
}

type QueryValue = LocationQueryValue | LocationQueryValue[] | undefined

const allValues = (value: QueryValue): string[] =>
  (Array.isArray(value) ? value : [value]).filter((v): v is string => typeof v === 'string')

const firstValue = (value: QueryValue): string | null => allValues(value)[0] ?? null

/** `'1,2'` ou chave repetida → itens, sem vazios. */
const listValues = (value: QueryValue): string[] =>
  allValues(value)
    .flatMap((raw) => raw.split(','))
    .map((part) => part.trim())
    .filter(Boolean)

const unique = <T>(list: T[]): T[] => [...new Set(list)]

const parseIds = (value: QueryValue): number[] =>
  unique(listValues(value).filter((part) => ID_PATTERN.test(part))).map(Number)

const parseSectorKey = (raw: string): string | null => {
  if (raw === NO_SECTOR) return 'none'
  return ID_PATTERN.test(raw) ? `sector:${raw}` : null
}

const parseSpecialtyKey = (raw: string): string | null => {
  if (raw === NO_SPECIALTY) return 'none'
  const match = raw.match(/^(oficial|empresa):([1-9]\d*)$/)
  if (!match) return null
  return `${match[1] === 'oficial' ? 'official' : 'company'}:${match[2]}`
}

const parseKeys = (value: QueryValue, parse: (raw: string) => string | null): string[] =>
  unique(
    listValues(value)
      .map(parse)
      .filter((key): key is string => key !== null)
  )

const parseStatus = (raw: string | null): VacancyStatusFilter =>
  raw === 'open' || raw === 'awaiting' ? raw : 'all'

const parseDays = (raw: string | null): number => {
  if (!raw || !/^\d+$/.test(raw)) return DEFAULT_VACANCY_VIEW_STATE.days
  const days = Number(raw)
  return days >= MIN_DAYS && days <= MAX_DAYS ? days : DEFAULT_VACANCY_VIEW_STATE.days
}

const parseGroupBy = (raw: string | null): VacancyGroupBy => {
  if (raw === 'setor') return 'sector'
  if (raw === 'unidade') return 'unit'
  return 'urgency'
}

/**
 * URL → estado. Valor único (`setor=12`, links da 2.5a) é uma lista de 1;
 * valores inválidos são descartados em silêncio.
 */
export function parseVacancyQuery(query: LocationQuery): VacancyViewState {
  const tab = firstValue(query.aba)
  return {
    status: parseStatus(firstValue(query.status)),
    days: parseDays(firstValue(query.dias)),
    unitIds: parseIds(query.unidade),
    scheduleIds: parseIds(query.escala),
    sectorKeys: parseKeys(query.setor, parseSectorKey),
    specialtyKeys: parseKeys(query.especialidade, parseSpecialtyKey),
    groupBy: parseGroupBy(firstValue(query.agrupar)),
    tab: (tab ? TAB_FROM_URL.get(tab) : undefined) ?? null
  }
}

/** `?vaga=<stationId>` do "Copiar link da vaga". */
export function parseVacancyDeepLink(query: LocationQuery): number | null {
  const raw = firstValue(query.vaga)
  return raw && ID_PATTERN.test(raw) ? Number(raw) : null
}

const serializeSectorKey = (key: string): string | null => {
  if (key === 'none') return NO_SECTOR
  return key.startsWith('sector:') ? key.slice('sector:'.length) : null
}

const serializeSpecialtyKey = (key: string): string | null => {
  if (key === 'none') return NO_SPECIALTY
  const [source, id] = key.split(':')
  const urlSource = SPECIALTY_SOURCE_TO_URL[source as keyof typeof SPECIALTY_SOURCE_TO_URL]
  return urlSource && id ? `${urlSource}:${id}` : null
}

const csv = (parts: Array<string | null>): string =>
  parts.filter((part): part is string => part !== null).join(',')

/** Estado → URL, omitindo o que está no padrão. */
export function serializeVacancyQuery(state: VacancyViewState): Record<string, string> {
  const query: Record<string, string> = {}
  if (state.status !== 'all') query.status = state.status
  if (state.days !== DEFAULT_VACANCY_VIEW_STATE.days) query.dias = String(state.days)
  if (state.unitIds.length) query.unidade = state.unitIds.join(',')
  if (state.scheduleIds.length) query.escala = state.scheduleIds.join(',')
  const sectors = csv(state.sectorKeys.map(serializeSectorKey))
  if (sectors) query.setor = sectors
  const specialties = csv(state.specialtyKeys.map(serializeSpecialtyKey))
  if (specialties) query.especialidade = specialties
  if (state.groupBy !== 'urgency') query.agrupar = GROUP_BY_TO_URL[state.groupBy]
  if (state.tab) query.aba = TAB_TO_URL[state.tab]
  return query
}

/** Query sem as chaves da Central (para mesclar com o estado serializado). */
export function omitVacancyQueryKeys(query: LocationQuery): LocationQuery {
  const rest: LocationQuery = { ...query }
  VACANCY_QUERY_KEYS.forEach((key) => delete rest[key])
  return rest
}

/** Valores marcados de um filtro, como texto (o `value` das opções do dropdown). */
export function selectedFilterValues(view: VacancyViewState, kind: VacancyFilterKind): string[] {
  if (kind === 'unit') return view.unitIds.map(String)
  if (kind === 'schedule') return view.scheduleIds.map(String)
  if (kind === 'sector') return [...view.sectorKeys]
  return [...view.specialtyKeys]
}

/** Troca a lista inteira de um filtro (ids inválidos de unidade/escala saem). */
export function withFilterValues(
  view: VacancyViewState,
  kind: VacancyFilterKind,
  values: string[]
): VacancyViewState {
  const ids = (): number[] => unique(values.filter((v) => ID_PATTERN.test(v))).map(Number)
  if (kind === 'unit') return { ...view, unitIds: ids() }
  if (kind === 'schedule') return { ...view, scheduleIds: ids() }
  if (kind === 'sector') return { ...view, sectorKeys: unique(values) }
  return { ...view, specialtyKeys: unique(values) }
}

/** Marca ou desmarca um valor (toggle do dropdown, ✕ do chip). */
export function toggleFilterValue(
  view: VacancyViewState,
  kind: VacancyFilterKind,
  value: string
): VacancyViewState {
  const current = selectedFilterValues(view, kind)
  const next = current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value]
  return withFilterValues(view, kind, next)
}

const idsWithPrefix = (keys: string[], prefix: string): string[] =>
  keys.filter((key) => key.startsWith(`${prefix}:`)).map((key) => key.slice(prefix.length + 1))

/** Estado → query do `GET /vacancy-center` (listas em CSV). Aba e agrupamento são só da tela. */
export function toVacancyApiQuery(state: VacancyViewState): VacancyCenterQuery {
  const query: VacancyCenterQuery = { days: state.days }
  if (state.status !== 'all') query.status = state.status
  if (state.unitIds.length) query.healthUnits = state.unitIds.join(',')
  if (state.scheduleIds.length) query.schedules = state.scheduleIds.join(',')

  const sectorIds = idsWithPrefix(state.sectorKeys, 'sector')
  if (sectorIds.length) query.sectorIds = sectorIds.join(',')
  if (state.sectorKeys.includes('none')) query.withoutSector = 'true'

  const officialIds = idsWithPrefix(state.specialtyKeys, 'official')
  const companyIds = idsWithPrefix(state.specialtyKeys, 'company')
  if (officialIds.length) query.specialtyIds = officialIds.join(',')
  if (companyIds.length) query.companySpecialtyIds = companyIds.join(',')
  if (state.specialtyKeys.includes('none')) query.withoutSpecialty = 'true'
  return query
}
